<template>
    <div class="scrollable-list">
        <template v-if="!isEmpty">
            <slot
                v-for="(item, index) in items"
                :key="item[keyProp] ?? index"
                :item="item"
                :index="index"
                name="item"
            />
        </template>
        <template v-else-if="!isScrolling">
            <slot name="empty" />
        </template>
        <div
            v-if="isScrollable"
            ref="triggerEl"
            data-test="scrollable-list-trigger"
            class="scrollable-list__trigger"
        />
        <template v-if="isScrolling">
            <slot
                v-for="i in skeletonCount"
                :key="i"
                name="skeleton"
            />
        </template>
        <template v-else-if="isEndSlotVisible">
            <slot name="end" />
        </template>
    </div>
</template>

<script setup lang="ts">
    import { computed, onUnmounted, ref, useSlots } from 'vue';
    import { useIntersectionObserver } from '@vueuse/core';

    type Item = Record<string, any>

    type ScrollableListProps = {
        items?: Item[];
        keyProp?: keyof Item;
        skeletonCount?: number;
        isScrollable?: boolean;
        isScrolling?: boolean;
    }

    const props = withDefaults(defineProps<ScrollableListProps>(), {
        items: () => [],
        keyProp: undefined,
        skeletonCount: 1,
        isScrollable: false,
        isScrolling: false,
    });

    const emit= defineEmits(['scroll']);

    const triggerEl = ref(null);
    const slots = useSlots();

    const isEmpty = computed(() => !props.items?.length);
    const isEndSlotVisible = computed(() => slots.end && !props.isScrollable && !isEmpty.value);

    const { stop } = useIntersectionObserver(triggerEl, ([{ isIntersecting }]) => isIntersecting && !props.isScrolling && emit('scroll'));
    onUnmounted(stop);
</script>

<style lang="scss" scoped>
.scrollable-list {
  &__trigger {
    width: 100%;
  }
}
</style>
